import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button, Field, IconButton, Input, Modal, useStyles } from '@grafana/ui';

import { css } from '@emotion/css';

import { OutlierDetails } from 'api/types';
import { validateName } from 'components/FormFields/validators';
import { onFormSubmit } from 'utils';
import { slugifyName } from 'utils/utils.jobs';

export interface SavePanelFields {
  id: string;
  name: string;
  description: string;
  metric: string;
}

interface OutlierSavePanelProps {
  isOpen: boolean;
  isEdit?: boolean;
  defaults?: OutlierDetails;
  onDismiss: () => void;
  onSaveOutlier: ({ name, description, metric }: SavePanelFields) => void;
}

export const OutlierSavePanel: React.FC<OutlierSavePanelProps> = ({
  isOpen,
  isEdit = false,
  defaults,
  onDismiss,
  onSaveOutlier,
}) => {
  const styles = useStyles(getStyles);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<SavePanelFields>({});
  const [showEditMetric, setShowEditMetric] = useState(false);

  const nameWatch = watch('name');
  const metricWatch = watch('metric');

  useEffect(() => {
    // watch name changes to update the auto-generated metric name
    if (nameWatch !== undefined && !showEditMetric) {
      const metricName = slugifyName(nameWatch);
      if (metricName !== getValues('metric')) {
        clearErrors('metric');
        setValue('metric', metricName, { shouldValidate: true, shouldDirty: true });
      }
    }
  }, [clearErrors, getValues, nameWatch, setValue, showEditMetric]);

  const onSave = useCallback(
    async (form: SavePanelFields) => {
      onSaveOutlier(form);
    },
    [onSaveOutlier]
  );

  return (
    <Modal
      className={styles.container}
      icon="copy"
      isOpen={isOpen}
      onDismiss={onDismiss}
      onClickBackdrop={onDismiss}
      title={`${isEdit ? 'Update' : 'Create'} outlier`}
    >
      <form onSubmit={onFormSubmit(handleSubmit(onSave))}>
        <Field label="Name" error="Please enter a valid name">
          <Input
            {...register('name', { required: true, value: defaults?.name })}
            data-testid="outlier-detector-name-input"
          />
        </Field>
        <Field label="Description" error="Please enter a valid description">
          <Input
            {...register('description', { required: false, value: defaults?.description })}
            data-testid="outlier-detector-description-input"
          />
        </Field>
        <Field
          label={
            showEditMetric ? (
              'Metric name'
            ) : (
              <label className={styles.metricLabel}>
                <span>Metric name: {metricWatch}</span>
                <IconButton
                  className={styles.editMetricIcon}
                  type="button"
                  form="Not-Default-For-This-Form"
                  name="edit"
                  size="sm"
                  tooltip="Edit the metric name"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowEditMetric(true);
                  }}
                />
              </label>
            )
          }
          invalid={Boolean(errors.metric)}
          error={errors.metric?.message}
        >
          {showEditMetric ? (
            <Input
              {...register('metric', {
                required: 'metric is required',
                validate: (name) => validateName(name),
              })}
            />
          ) : (
            <input
              type="hidden"
              {...register('metric', {
                required: 'metric is required',
                validate: (name) => validateName(name),
              })}
            />
          )}
        </Field>
        <Button type="submit" data-testid="create-outlier-detector-button">
          {isEdit ? 'Update outlier' : 'Create outlier'}
        </Button>
      </form>
    </Modal>
  );
};

function getStyles() {
  return {
    container: css`
      width: 500px;
    `,
    metricLabel: css`
      font-size: 12px;
      font-weight: 500;
      line-height: 1.25;
      display: inline-flex;
      align-items: center;
    `,
    editMetricIcon: css`
      margin-left: 10px;
    `,
  };
}
